<script setup lang="ts">
const VALID_ROUTES = [
  '/wishlist',
  '/my-account/wishlist',
  '/checkout/thank-you',
  '/stores',
  '/stores/*',
  '/customer-service/contact-us',
  '/my-account',
  '/my-account/order-history',
  '/my-account/register',
  '/my-account/order-history/{*}/pcp/{*}',
]

const SECTIONS_MAP = {
  header: 'headerSlots',
  footer: 'footerSlots',
}

type PageContentSlotProps = {
  slug: string
  section: 'header' | 'footer'
}

type ValidatedRoute = {
  valid: boolean
  contentRoute: string
}
const props = defineProps<PageContentSlotProps>()
const locale = useI18n().locale.value

/*
  *  = custom content page with the * wildcard matched to what the actual route is
 {*} = custom content page with the actual route ignored so that all content will be exactly the same no matter the wildcards values
*/
const isSlugValidRoute = (slug: string, validRoute: string): ValidatedRoute => {
  if (validRoute.includes('{*}')) {
    const regexPattern = validRoute.replace(/{\*}/g, '[^/]+')
    const regex = new RegExp(`^${regexPattern}$`)
    return { valid: regex.test(slug), contentRoute: validRoute }
  }
  if (validRoute.endsWith('*')) {
    // Handle wildcard routes like /stores/* ( /stores/michael-hill-perth == /stores/* )
    return { valid: slug.startsWith(validRoute.slice(0, -1)), contentRoute: slug }
  }
  return { valid: validRoute === slug, contentRoute: slug }
}

function validateSlug(slug: string): ValidatedRoute | undefined {
  for (const validRoute of VALID_ROUTES) {
    const route = isSlugValidRoute(slug, validRoute)
    if (route.valid) return route
  }
}

const validatedSlug = validateSlug(props.slug.replace(/^\/fr\//, '/'))
const key = `pageContentSlots__${props.section}__${validatedSlug?.contentRoute}`
let data: any = null

if (validatedSlug) {
  ;({ data } = await useAsyncData(key, handler, {
    deep: false,
    transform: transformer,
  }))
}

async function handler() {
  const { getEntries } = useContentful()
  const entries = await getEntries({
    content_type: 'pageContentSlots',
    include: 10,
    limit: 1,
    locale: localeConfig[locale].code,
    'fields.slug': validatedSlug?.contentRoute,
  })
  return entries?.items
}

function transformer(data: any) {
  const fields = data?.[0]?.fields
  const slot = fields[SECTIONS_MAP[props.section]] || []

  // Transform to PanelSwitcher data format
  const panels = slot.map((panel: any) => {
    const id = panel?.sys?.id
    const componentName = panel?.sys?.contentType?.sys?.id
    const fields = panel?.fields || {} /* supportingData, personalizationTags, other attrs */

    return {
      id,
      componentName,
      ...fields,
    }
  })

  return panels
}
</script>

<template>
  <section class="page-content-slot" :class="`page-content-slot--${props.section}`">
    <PanelSwitcher class="page-content-slot__panel" v-for="(panel, i) in data" :key="i" v-bind="panel" />
  </section>
</template>

<style lang="postcss">
.page-content-slot {
  .page-content-slot__panel.rich-text-panel {
    padding-block: var(--space-100);

    @media (--tablet) {
      padding-block: var(--space-120);
    }
  }

  > :last-child {
    margin-bottom: 0; /* Reset bottom margin --component-space */
  }
}
</style>
